import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnDestroy, OnInit, signal } from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { FileUploadResult, FileUploadService } from '../../../../services/file-upload.service';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { loadCurrentUser } from '../../../../state/auth/auth.actions';
import { createBrand, updateBrand } from '../../../../state';

interface BrandForm {
  brandName: FormControl<string>;
  primaryColor: FormControl<string>;
  website: FormControl<string>;
  description: FormControl<string>;
}

@Component({
  selector: 'wil-admin-tab-brand',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatIconModule
],
  templateUrl: './admin-tab-brand.component.html',
  styleUrl: './admin-tab-brand.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTabBrandComponent implements OnInit, OnDestroy {
  private readonly fileUploadService = inject(FileUploadService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  public readonly brand = input<ProfessionalBrandOutputDto | null>(null);

  public readonly brandForm;

  // Logo upload state
  public readonly logoFile = signal<File | null>(null);
  public readonly logoPreview = signal<string>('');
  public readonly logoBase64 = signal<string>('');
  public readonly isUploading = signal(false);
  public readonly uploadError = signal<string | null>(null);

  // Save state
  public readonly isSaving = signal(false);
  public readonly saveError = signal<string | null>(null);
  public readonly saveSuccess = signal(false);

  // Drag and drop state
  public readonly isDragOver = signal(false);

  private readonly saveClick$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.brandForm = new FormGroup<BrandForm>({
      brandName: new FormControl(this.brand()?.brandName || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2)],
      }),
      primaryColor: new FormControl(this.brand()?.primaryColor || '#3f51b5', { nonNullable: true }),
      website: new FormControl(this.brand()?.website || '', { nonNullable: true }),
      description: new FormControl(this.brand()?.description || '', { nonNullable: true }),
    });

    // Automatically patch form whenever brand input changes
    effect(() => {
      const data = this.brand();
      if (data) {
        this.brandForm.patchValue(data);
      }
    });

    // Auto-hide success message using effect in constructor (injection context)
    effect(() => {
      if (this.saveSuccess()) {
        setTimeout(() => {
          this.saveSuccess.set(false);
        }, 3000);
      }
    });

    effect(() => {
      const data = this.brand();
      if (data?.logoUrl) {
        this.logoPreview.set(data.logoUrl);
        this.logoBase64.set(data.logoUrl);
      }
    });
  }

  public async ngOnInit(): Promise<void> {
    // Handle save clicks with latest categories and account ID
    this.saveClick$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
      await this.handleSave();
    });

    this.store.dispatch(loadCurrentUser());
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up blob URLs
    const previewUrl = this.logoPreview();
    if (previewUrl && previewUrl.startsWith('blob:')) {
      this.fileUploadService.revokePreviewUrl(previewUrl);
    }
  }

  public onSave(): void {
    if (this.brandForm.valid) {
      this.saveClick$.next();
    }
  }

  // File upload methods
  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processSelectedFile(input.files[0]);
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const files = event.dataTransfer?.files;
    const validationError = this.fileUploadService.validateDroppedFiles(files || null, {
      maxSizeBytes: 2 * 1024 * 1024, // 2MB
      allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'],
    });

    if (validationError) {
      this.uploadError.set(validationError.message);
      return;
    }

    if (files && files.length > 0) {
      this.processSelectedFile(files[0]);
    }
  }

  private async processSelectedFile(file: File): Promise<void> {
    this.isUploading.set(true);
    this.uploadError.set(null);

    try {
      const result: FileUploadResult = await this.fileUploadService.processLogoFile(file);

      if (result.success && result.base64) {
        // Clean up previous preview URL if it was a blob
        const currentPreview = this.logoPreview();
        if (currentPreview && currentPreview.startsWith('blob:')) {
          this.fileUploadService.revokePreviewUrl(currentPreview);
        }

        this.logoFile.set(file);
        this.logoBase64.set(result.base64);
        this.logoPreview.set(result.base64);
        this.uploadError.set(null);
      } else if (result.error) {
        this.uploadError.set(result.error.message);
      }
    } catch (error) {
      console.error('Error processing file:', error);
      this.uploadError.set('Failed to process the selected file. Please try again.');
    } finally {
      this.isUploading.set(false);
    }
  }

  public removeLogo(): void {
    const currentPreview = this.logoPreview();
    if (currentPreview && currentPreview.startsWith('blob:')) {
      this.fileUploadService.revokePreviewUrl(currentPreview);
    }

    this.logoFile.set(null);
    this.logoBase64.set('');
    this.logoPreview.set('');
    this.uploadError.set(null);
  }

  // Form methods
  public async handleSave(): Promise<void> {
    if (this.brandForm.invalid) {
      this.brandForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    this.saveError.set(null);
    this.saveSuccess.set(false);

    const formValue = this.brandForm.value;
    const logoFile = this.logoFile();

    const brand: CreateProfessionalBrandDto = {
      brandName: formValue.brandName!,
      primaryColor: formValue.primaryColor || '#3f51b5',
      website: formValue.website || undefined,
      description: formValue.description || undefined,
      logoUrl: this.logoBase64() || undefined,
      logoFileName: logoFile?.name || undefined,
    };

    if (this.brand()) {
      this.store.dispatch(updateBrand({ brand }));
    } else {
      this.store.dispatch(createBrand({ brand }));
    }

    this.saveSuccess.set(true);
    this.isSaving.set(false);

    // how to handle error
    // console.error('Error saving brand:', error);
    this.saveError.set('Failed to save brand settings. Please try again.');
  }

  public onReset(): void {
    this.brandForm.reset({
      brandName: '',
      primaryColor: '#3f51b5',
      website: '',
      description: '',
    });
    this.removeLogo();
    this.saveError.set(null);
    this.saveSuccess.set(false);
  }

  // Helper methods
  public getFieldError(fieldName: keyof BrandForm): string | null {
    const field = this.brandForm.get(fieldName);
    if (!field || !field.errors || !field.touched) {
      return null;
    }

    if (field.errors['required']) {
      return `${this.getFieldLabel(fieldName)} is required`;
    }

    if (field.errors['minlength']) {
      return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
    }

    return null;
  }

  private getFieldLabel(fieldName: keyof BrandForm): string {
    const labels: Record<keyof BrandForm, string> = {
      brandName: 'Brand name',
      primaryColor: 'Primary color',
      website: 'Website',
      description: 'Description',
    };

    return labels[fieldName];
  }
}
